import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import ForgotPassword from './pages/ForgotPassword';
import Calendar from './pages/Calendar';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Excom from './pages/Excom/Excom';
import ExcomMeetingDetail from './pages/Excom/MeetingDetail';
import Meetings from './pages/Meetings';
import MeetingDetail from './pages/MeetingDetail';
import GovernanceHub from './pages/Governance';
import GovernanceRecordDetail from './pages/Governance/RecordDetail';

// Layout
import Layout from './components/Layout';

/** Shared full-page loading state for auth-gated routes. */
const RouteFallback = () => (
    <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg-body)' }}
        role="status"
        aria-label="Loading"
    >
        <span className="w-7 h-7 rounded-full border-2 border-[var(--border-strong)] border-t-[var(--primary)] animate-spin" />
    </div>
);

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <RouteFallback />;
    }

    return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <RouteFallback />;
    }

    return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
    const { user, isAuthenticated, loading } = useAuth();
    const adminRoles = ['Manager', 'Head of Department', 'Developer'];

    if (loading) {
        return <RouteFallback />;
    }

    const isGlobalAdmin = user && adminRoles.includes(user.role);

    if (!isAuthenticated) return <Navigate to="/login" />;
    if (!isGlobalAdmin) return <Navigate to="/projects" />;

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                }
            />

            {/* Protected Routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/projects" />} />
                <Route
                    path="dashboard"
                    element={
                        <AdminRoute>
                            <Dashboard />
                        </AdminRoute>
                    }
                />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="tasks/:id" element={<TaskDetail />} />
                <Route
                    path="reports"
                    element={
                        <AdminRoute>
                            <Reports />
                        </AdminRoute>
                    }
                />
                <Route path="users" element={
                    <AdminRoute>
                        <Users />
                    </AdminRoute>
                } />
                <Route path="profile" element={<Profile />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="tickets" element={<Tickets />} />
                <Route path="tickets/:id" element={<TicketDetail />} />
                <Route path="meetings" element={<Meetings />} />
                <Route path="meetings/:id" element={<MeetingDetail />} />
                <Route path="governance" element={<GovernanceHub />} />
                <Route path="governance/:id" element={<GovernanceRecordDetail />} />
                <Route path="excom" element={<Excom />} />
                <Route path="excom/:id" element={<ExcomMeetingDetail />} />
            </Route>

            {/* 404 Route - Redirect to projects since dashboard is restricted */}
            <Route path="*" element={<Navigate to="/projects" />} />
        </Routes>
    );
}


function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <Router>
                    <AppRoutes />
                </Router>
                <Toaster
                    position="top-right"
                    gutter={8}
                    toastOptions={{
                        duration: 3500,
                        style: {
                            background: '#ffffff',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-dropdown)',
                            fontSize: 'var(--fs-body)',
                            padding: '10px 12px',
                            maxWidth: '360px',
                        },
                        success: { iconTheme: { primary: 'var(--success)', secondary: '#fff' } },
                        error: { duration: 5000, iconTheme: { primary: 'var(--danger)', secondary: '#fff' } },
                    }}
                />
            </AuthProvider>
        </QueryClientProvider>
    );
}

export default App;
